import React, { useEffect, useMemo, useState } from 'react';
import {
  Play, ClipboardCheck, UserCheck, UploadCloud, Inbox, Search, HelpCircle,
  Clock, Edit3, ShieldCheck, PenTool, Send, CloudUpload, CheckCircle,
  CheckCircle2, Archive, Circle, AlertTriangle, AlertCircle, ChevronDown,
  ChevronUp, ExternalLink, MessageSquare, Sparkles,
} from 'lucide-react';

/* ------------------------------------------------------------------ icons */
const ICONS = {
  play: Play, 'clipboard-check': ClipboardCheck, 'user-check': UserCheck,
  'upload-cloud': UploadCloud, inbox: Inbox, search: Search,
  'help-circle': HelpCircle, clock: Clock, 'edit-3': Edit3,
  'shield-check': ShieldCheck, 'pen-tool': PenTool, send: Send,
  'cloud-upload': CloudUpload, 'check-circle': CheckCircle,
  'check-circle-2': CheckCircle2, archive: Archive, circle: Circle,
};

function StageIcon({ code, className = '' }) {
  const C = ICONS[code] || Circle;
  return <C className={className} />;
}

/* ------------------------------------------------------------------ relative time */
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-CA', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/* ------------------------------------------------------------------ stage row */
function StageDot({ status }) {
  // status: completed | current | attention | pending | error
  const base = 'w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-300';
  if (status === 'completed') {
    return (
      <div className={`${base} bg-emerald-500 border-emerald-600 text-white shadow-[0_4px_12px_-2px_rgba(16,185,129,0.4)]`}>
        <CheckCircle className="w-5 h-5" />
      </div>
    );
  }
  if (status === 'attention') {
    return (
      <div className={`${base} bg-[#f5b300] border-[#e0a200] text-[#14110f] shadow-[0_4px_16px_-2px_rgba(245,179,0,0.55)] animate-pulse-soft motion-reduce:animate-none`}>
        <AlertTriangle className="w-5 h-5" />
      </div>
    );
  }
  if (status === 'current') {
    return (
      <div className={`${base} bg-[#0ea5e9] border-[#0284c7] text-white shadow-[0_4px_16px_-2px_rgba(14,165,233,0.5)] animate-pulse-soft motion-reduce:animate-none`}>
        <Sparkles className="w-4 h-4" />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={`${base} bg-rose-500 border-rose-600 text-white`}>
        <AlertCircle className="w-5 h-5" />
      </div>
    );
  }
  return (
    <div className={`${base} bg-white border-neutral-200 text-neutral-300`}>
      <Circle className="w-3.5 h-3.5 fill-current" />
    </div>
  );
}

function StageRow({ stage, isLast, expanded, onToggle, onAction }) {
  const status = stage.status; // completed | current | attention | pending
  const connectorClass =
    status === 'completed'
      ? 'bg-gradient-to-b from-emerald-500 to-emerald-400'
      : status === 'current' || status === 'attention'
        ? 'bg-gradient-to-b from-[#f5b300]/70 to-neutral-200'
        : 'bg-neutral-200';

  const titleColor =
    status === 'pending' ? 'text-neutral-500'
      : status === 'attention' ? 'text-[#7c5a00]'
        : 'text-[#14110f]';

  const cardClass =
    status === 'current' || status === 'attention'
      ? 'bg-gradient-to-br from-[#fff8e3] to-white border-[#f5b300]/60 shadow-[0_10px_30px_-15px_rgba(245,179,0,0.4)]'
      : status === 'completed'
        ? 'bg-white border-neutral-100'
        : 'bg-neutral-50/40 border-neutral-100';

  return (
    <li className="relative flex gap-4" data-testid={`stage-row-${stage.code}`}>
      {/* vertical connector */}
      <div className="flex flex-col items-center shrink-0">
        <StageDot status={status} />
        {!isLast && <div className={`flex-1 w-[3px] my-1 rounded-full ${connectorClass}`} aria-hidden="true" />}
      </div>

      <div className="pb-7 flex-1 min-w-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          data-testid={`stage-toggle-${stage.code}`}
          className={`w-full text-left rounded-lg border ${cardClass} px-4 py-3 transition-all duration-200 hover:shadow-md`}
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5 text-[#c89200]">
              <StageIcon code={stage.icon} className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className={`text-[14.5px] font-bold ${titleColor}`}>{stage.name}</h4>
                {status === 'attention' && (
                  <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#f5b300] text-[#14110f]">Action needed</span>
                )}
                {status === 'current' && (
                  <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0ea5e9] text-white">In progress</span>
                )}
                {status === 'completed' && stage.entered_at && (
                  <span className="text-[11px] text-neutral-400 font-medium">· {relTime(stage.entered_at)}</span>
                )}
              </div>
              <p className={`text-[12.5px] mt-1 ${status === 'pending' ? 'text-neutral-400' : 'text-neutral-600'}`}>
                {stage.description}
              </p>
              {stage.notes?.length > 0 && (
                <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] text-[#c89200] font-semibold">
                  <MessageSquare className="w-3 h-3" /> {stage.notes.length} note{stage.notes.length === 1 ? '' : 's'}
                </p>
              )}
            </div>
            {(stage.notes?.length > 0 || stage.entered_at) && (
              <span className="text-neutral-300 shrink-0 mt-0.5">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
            )}
          </div>

          {expanded && (
            <div className="mt-3 pt-3 border-t border-neutral-100 space-y-2 text-[12.5px] text-neutral-700">
              {stage.entered_at && (
                <p className="text-neutral-500">
                  <span className="font-semibold text-neutral-600">Reached:</span> {fmtDateTime(stage.entered_at)}
                  {stage.entered_by ? <> · by {stage.entered_by}</> : null}
                </p>
              )}
              {stage.notes?.length > 0 && (
                <ul className="space-y-2">
                  {stage.notes.map((n) => (
                    <li key={n.id} className="rounded-md bg-neutral-50 px-3 py-2 border border-neutral-100">
                      <p className="text-[11px] text-neutral-500 mb-0.5">{n.actor_name || 'Specialist'} · {fmtDateTime(n.created_at)}</p>
                      <p className="text-[13px] text-neutral-800 whitespace-pre-line">{n.note}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </button>

        {/* CTA strip for action-required current stage */}
        {status === 'attention' && onAction && (
          <div className="mt-2.5 ml-1 flex flex-wrap gap-2">
            {stage.code === 'documents_requested' || stage.code === 'additional_info_requested' ? (
              <button onClick={() => onAction('upload', stage)} data-testid={`stage-cta-upload-${stage.code}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-[#14110f] hover:bg-[#291d12] text-white px-3.5 py-2 rounded-md transition">
                <UploadCloud className="w-3.5 h-3.5" /> Upload documents
              </button>
            ) : null}
            {stage.code === 'signature_requested' || stage.code === 'awaiting_signature' ? (
              <button onClick={() => onAction('sign', stage)} data-testid={`stage-cta-sign-${stage.code}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-[#14110f] hover:bg-[#291d12] text-white px-3.5 py-2 rounded-md transition">
                <PenTool className="w-3.5 h-3.5" /> Review & sign
              </button>
            ) : null}
            <button onClick={() => onAction('view-tax-file', stage)} data-testid={`stage-cta-view-${stage.code}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold border border-neutral-200 hover:border-neutral-300 bg-white text-[#14110f] px-3.5 py-2 rounded-md transition">
              View tax file <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

/* ============================================================ Tracker */
export default function TaxFilingProgressTracker({
  timeline,
  loading = false,
  onAction = null,
  className = '',
  compact = false,
}) {
  const [openIds, setOpenIds] = useState({});

  useEffect(() => {
    // Auto-expand current/attention stages.
    if (!timeline?.stages) return;
    const next = {};
    timeline.stages.forEach((s) => {
      if (s.status === 'current' || s.status === 'attention') next[s.code] = true;
    });
    setOpenIds(next);
  }, [timeline?.current_stage_code]); // eslint-disable-line

  const stages = timeline?.stages || [];
  const completedCount = useMemo(() => stages.filter((s) => s.status === 'completed').length, [stages]);
  const total = stages.length;
  const pct = total ? Math.round((completedCount / total) * 100) : 0;
  const current = stages.find((s) => s.code === timeline?.current_stage_code);

  if (loading) {
    return (
      <div className={`rounded-2xl border border-neutral-100 bg-white p-6 ${className}`} data-testid="timeline-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-neutral-100 rounded w-2/3" />
          <div className="h-2 bg-neutral-100 rounded w-full" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-9 h-9 rounded-full bg-neutral-100" />
              <div className="flex-1 h-14 bg-neutral-50 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!stages.length) {
    return (
      <div className={`rounded-2xl border border-neutral-100 bg-white p-6 text-center text-neutral-500 ${className}`} data-testid="timeline-empty">
        No workflow stages configured yet.
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-neutral-100 bg-white shadow-[0_25px_60px_-35px_rgba(20,17,15,0.25)] overflow-hidden ${className}`} data-testid="tax-filing-tracker">
      {/* Header */}
      <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-[#14110f] to-[#291d12] text-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#f5b300] font-bold mb-1">Tax Filing Progress</p>
            <h2 className="text-[20px] md:text-[22px] font-extrabold leading-tight">
              {current ? current.name : 'Getting started'}
            </h2>
            {current && (
              <p className="text-[13px] text-white/70 mt-1.5 max-w-xl">
                {current.description}
                {timeline?.assigned_specialist?.name && (
                  <> Your specialist: <span className="text-white font-semibold">{timeline.assigned_specialist.name}</span>.</>
                )}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-bold">Tax year</p>
            <p className="text-[24px] font-extrabold text-[#f5b300]">{timeline?.tax_year}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] text-white/70 mb-1.5">
            <span className="font-semibold uppercase tracking-wider">{completedCount} of {total} stages complete</span>
            <span className="text-[#f5b300] font-bold">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#f5b300] to-[#e0a200] transition-all duration-700"
                  style={{ width: `${pct}%` }} aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Vertical timeline */}
      <div className="px-5 md:px-6 pt-6 pb-2">
        <ul className="relative" role="list">
          {(compact ? stages.filter((s, i) => s.status !== 'pending' || i <= stages.findIndex(x => x.code === timeline?.current_stage_code) + 2) : stages).map((s, idx, arr) => (
            <StageRow
              key={s.id || s.code}
              stage={s}
              isLast={idx === arr.length - 1}
              expanded={!!openIds[s.code]}
              onToggle={() => setOpenIds((p) => ({ ...p, [s.code]: !p[s.code] }))}
              onAction={onAction}
            />
          ))}
        </ul>
      </div>

      <style>{`
        @keyframes pulseSoft { 0%,100% { box-shadow: 0 0 0 0 currentColor; opacity: 1; } 50% { box-shadow: 0 0 0 6px transparent; opacity: 0.92; } }
        .animate-pulse-soft { animation: pulseSoft 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
